import React from 'react'
import { Controller } from 'react-hook-form'
import { ToggleButton } from '@components/toggleButton/toggleButton.tsx'
import styles from './AccountSettings.module.scss'
import { cn } from '@src/utils/cn'
import { LxSelect } from '@src/components/select/select'
import camelCase from 'camelcase'

const FIELD_TYPES = {
  text: 'text',
  number: 'number',
  select: 'select',
  boolean: 'boolean',
} as const

export interface AccountSettingFieldProps {
  settingKey: string
  setting: any
  control: any
  editorLevel: 'school' | 'district' | 'vendor'
  onEnforceChange?: (settingKey: string, enforced: boolean) => void
  isEnforced?: boolean
}

const ReadOnlyField: React.FC<{ label?: string; description?: string; value: any; enforcedBy?: string }> = ({ label, description, value, enforcedBy }) => (
  <div className={cn(styles.readOnlyField, styles.lockedField)}>
    <label>
      🔒 {label}{' '}
      <span className={styles.helperText} title={`This setting is enforced by the ${enforcedBy}.`}>
        (Read Only)
      </span>
    </label>
    {description && <div className={styles.helperText}>{description}</div>}
    <div className={styles.valueText}>{camelCase(String(value), { pascalCase: true })}</div>
  </div>
)

// Helper for label formatting
function toNiceLabel(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\sAnd\s/g, ' and ')
}

// Enforcement rules
function getFieldAccess(setting: any, editorLevel: 'vendor' | 'district' | 'school') {
  // vendor can edit and enfoce all settings
  // district can edit and enforce  settings, if not enforced by vendor settings
  // school can edit school settings which is not enforced by vendor or district

  const enforcedBy = setting.enforced_by

  const isVendor = editorLevel === 'vendor'
  const isDistrict = editorLevel === 'district'
  const isSchool = editorLevel === 'school'

  // Vendor can always edit and enforce
  if (isVendor) {
    return {
      readOnly: false,
      showEnforceCheckbox: true,
    }
  }

  // District: Cannot edit if vendor enforced
  if (isDistrict) {
    if (enforcedBy === 'vendor') {
      return {
        readOnly: true,
        enforcedBy: 'vendor',
      }
    }

    // return {
    //   readOnly: false,
    //   showEnforceCheckbox: true, // Can enforce if not vendor-enforced
    // }
    // for now there is no enforce slider will be shown, all the settings are enforced to teh schools in the backend
    return {
      readOnly: false,
      showEnforceCheckbox: false, // Can enforce if not vendor-enforced
    }
  }

  // School: Can only edit if not enforced at any level
  if (isSchool) {
    if (enforcedBy === 'vendor' || enforcedBy === 'district') {
      return {
        readOnly: true,
        enforcedBy,
      }
    }

    return {
      readOnly: false,
    }
  }

  // Fallback (shouldn't happen)
  return {
    readOnly: true,
  }
}

export const AccountSettingField: React.FC<AccountSettingFieldProps> = ({ settingKey, setting, control, editorLevel, onEnforceChange, isEnforced }) => {
  const { element, label, description } = setting.ui || {}
  const type = (element || '').toLowerCase()
  const options: string[] = type === 'select' ? setting.ui?.items?.filter((v: string) => !!v) ?? [] : []

  const access = getFieldAccess(setting, editorLevel)

  if (access.readOnly) {
    return <ReadOnlyField label={label} description={description} value={setting.effective_value} enforcedBy={access.enforcedBy} />
  }

  const enforceCheckboxValue = isEnforced !== undefined ? isEnforced : setting.enforced_by === editorLevel

  const renderInput = () => {
    switch (type) {
      case FIELD_TYPES.text:
        return (
          <Controller
            name={settingKey}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <label htmlFor={settingKey}>{label}</label>
                {description && <div className={styles.helperText}>{description}</div>}
                <div className={cn(styles.inputField, 'lxActionButton', 'inputHighLight', { ['formFieldRequired']: !!error })}>
                  <input className='pristineInput' type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={label || settingKey} />
                </div>
              </>
            )}
          />
        )

      case FIELD_TYPES.number:
        return (
          <Controller
            name={settingKey}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <label htmlFor={settingKey}>{label}</label>
                {description && <div className={styles.helperText}>{description}</div>}
                <div className={cn(styles.inputField, 'lxActionButton', 'inputHighLight', { ['formFieldRequired']: !!error })}>
                  <input
                    className='pristineInput'
                    min={0}
                    type={type}
                    step={1}
                    value={value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                      // Allow only digits (no decimal, no minus, no e)
                      if (/^\d*$/.test(val)) {
                        onChange(val === '' ? '' : Number(val))
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === '.' || e.key.toLowerCase() === 'e') {
                        e.preventDefault() // block negatives, decimals, scientific notation
                      }
                    }}
                    placeholder={label || settingKey}
                  />
                </div>
              </>
            )}
          />
        )

      case FIELD_TYPES.select:
        return (
          <Controller
            name={settingKey}
            control={control}
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <label htmlFor={settingKey}>{label}</label>
                {description && <div className={styles.helperText}>{description}</div>}
                <LxSelect
                  value={value ?? ''}
                  hasError={!!fieldState.error}
                  shrinked
                  placeholder={label || settingKey}
                  onChange={onChange}
                  options={options.map((opt) => ({ value: opt, label: toNiceLabel(opt) }))}
                  helperText={description}
                />
              </>
            )}
          />
        )

      case FIELD_TYPES.boolean:
        return (
          <Controller
            name={settingKey}
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className={styles.toggleRow}>
                <div className={styles.toggleLabelDesc}>
                  <label htmlFor={settingKey}>{label}</label>
                  {description && <div className={styles.helperText}>{description}</div>}
                </div>
                <div className={styles.toggleBtn}>
                  <ToggleButton value={Boolean(value)} onChange={onChange} ariaLabel={label || settingKey} />
                </div>
              </div>
            )}
          />
        )

      default:
        return null
    }
  }

  return (
    <>
      {renderInput()}
      {access.showEnforceCheckbox && (
        <div className={styles.toggleRow}>
          <div className={styles.toggleLabelDesc}>
            <label className={styles.enforceLabel}>Enforce this setting</label>
            <div className={styles.helperText}>Enforcing this setting will make the setting not editable by {editorLevel === 'vendor' ? 'districts and schools' : 'schools'}.</div>
          </div>
          <div className={styles.toggleBtn}>
            <ToggleButton value={enforceCheckboxValue} onChange={(checked) => onEnforceChange?.(settingKey, checked)} ariaLabel={'Enforce ' + label || settingKey} />
          </div>
        </div>
      )}
    </>
  )
}

export default AccountSettingField
